import type { DeploymentMetrics, Package } from "../api/deployments.api";
import type { components } from "@/types/api";

export type UpdateMetrics = components["schemas"]["UpdateMetrics"];

const OTA_LABEL = /^v\d+$/;

export function isOtaLabel(key: string): boolean {
  return OTA_LABEL.test(key);
}

export function finiteCount(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function failureRate(metrics: UpdateMetrics | undefined): number | null {
  if (!metrics) return null;
  const installed = finiteCount(metrics.installed);
  const failed = finiteCount(metrics.failed);
  if (installed === null || failed === null) return null;
  return failed / Math.max(installed + failed, 1);
}

export function formatFailureRatePercent(rate: number | null): string | null {
  if (rate === null) return null;
  return `${(rate * 100).toFixed(1)}%`;
}

export function totalActive(metrics: DeploymentMetrics): number {
  let sum = 0;
  for (const entry of Object.values(metrics)) {
    const active = finiteCount(entry.active);
    if (active !== null) sum += active;
  }
  return sum;
}

export function relatedReportingLabels(
  current: Pick<Package, "label" | "packageHash"> | undefined,
  history: Array<Pick<Package, "label" | "packageHash">>,
): string[] {
  if (!current?.label || !current.packageHash) return [];
  const related = new Set<string>();
  for (const pkg of history) {
    if (pkg.label && pkg.label !== current.label && pkg.packageHash === current.packageHash) {
      related.add(pkg.label);
    }
  }
  return [...related];
}

export type DistributionRow =
  | { kind: "release"; label: string; metrics: UpdateMetrics }
  | { kind: "unknown"; key: string; metrics: UpdateMetrics }
  | { kind: "binary"; key: string; metrics: UpdateMetrics };

export function shouldShowMetricsPanel(access: {
  serverCapabilities: boolean;
  can: (capability: "metrics.read") => boolean;
}): boolean {
  return !(access.serverCapabilities && !access.can("metrics.read"));
}

export function distributionRows(
  metrics: DeploymentMetrics,
  newestFirstLabels: string[],
  historyReady: boolean,
): DistributionRow[] {
  if (!historyReady) return [];

  const seen = new Set<string>();
  const rows: DistributionRow[] = [];

  for (const label of newestFirstLabels) {
    const entry = metrics[label];
    if (!entry) continue;
    seen.add(label);
    rows.push({ kind: "release", label, metrics: entry });
  }

  for (const [key, entry] of Object.entries(metrics)) {
    if (seen.has(key)) continue;
    rows.push({
      kind: isOtaLabel(key) ? "unknown" : "binary",
      key,
      metrics: entry,
    });
  }

  return rows;
}
